/**
 * API Client for Rocket Tronc Backend
 * Handles all HTTP requests with proper error handling, auth tokens, and response parsing.
 */

const TOKEN_KEY = "rocket_tronc_token";
const SELECTED_TENANT_KEY = "rocket_tronc_selected_tenant";

/**
 * Get the API base URL from environment or use default
 */
export const getApiBaseUrl = () => {
  // In development, Vite proxy handles /api routes
  if (import.meta.env.DEV) {
    return "/api";
  }
  // In production, use the configured API URL
  return import.meta.env.VITE_API_URL || "/api";
};

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

/**
 * Get auth headers including tenant context
 */
const getAuthHeaders = () => {
  const headers = {};

  // Add auth token
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    console.log(
      "[API Client] Using auth token:",
      token.substring(0, 20) + "...",
    );
    headers.Authorization = `Bearer ${token}`;
  } else {
    console.log("[API Client] No auth token found");
  }

  // Add selected tenant ID for Global Admins
  const selectedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);
  if (selectedTenantId) {
    console.log("[API Client] Using selected tenant:", selectedTenantId);
    headers["X-Tenant-Id"] = selectedTenantId;
  }

  return headers;
};

/**
 * Main API client with methods for all HTTP verbs
 */
export const apiClient = {
  baseUrl: getApiBaseUrl(),

  /**
   * Make a fetch request with error handling
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    console.log(`[API Client] ${options.method || "GET"} ${url}`);

    const config = {
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      console.log(`[API Client] Response ${response.status} for ${endpoint}`);

      // Handle 401 Unauthorized
      if (response.status === 401) {
        console.log(
          "[API Client] 401 Unauthorized - clearing auth and redirecting",
        );

        // Don't intercept 401 on auth endpoints - let the caller handle it
        const isAuthEndpoint =
          endpoint.startsWith("/auth/login") ||
          endpoint.startsWith("/auth/refresh") ||
          endpoint.startsWith("/auth/forgot-password") ||
          endpoint.startsWith("/auth/reset-password") ||
          endpoint.startsWith("/auth/validate-reset-token") ||
          endpoint.startsWith("/users/invitations/");

        if (isAuthEndpoint) {
          // Parse and return the error from the server so the login form can display it
          let data;
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            data = await response.json();
          }
          const errorMessage =
            data?.message || data?.Message || "Authentication failed.";
          throw new ApiError(errorMessage, 401, data);
        }

        // Only redirect if we're not already on login page
        const currentPath = window.location.pathname;
        console.log("[API Client] Current path:", currentPath);

        if (currentPath !== "/login") {
          // Clear stored auth data
          console.log("[API Client] Clearing stored auth data");
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem("rocket_tronc_refresh_token");
          localStorage.removeItem("rocket_tronc_user");

          // Use setTimeout to avoid redirect during React render
          setTimeout(() => {
            console.log("[API Client] Redirecting to /login");
            window.location.href = "/login";
          }, 100);
        }
        throw new ApiError("Session expired. Please log in again.", 401);
      }

      // Handle 429 Too Many Requests
      if (response.status === 429) {
        const retryAfter = response.headers.get("retry-after");
        let data;
        const ct = response.headers.get("content-type");
        if (ct && ct.includes("application/json")) {
          data = await response.json();
        }

        const retrySeconds = retryAfter ? parseInt(retryAfter, 10) : 300;
        const retryMinutes = Math.ceil(retrySeconds / 60);
        const message =
          data?.message ||
          `Too many requests. Please try again in ${retryMinutes} minute${
            retryMinutes !== 1 ? "s" : ""
          }.`;

        console.warn(
          `[API Client] 429 Rate Limited for ${endpoint}. Retry after ${retrySeconds}s`,
        );

        const error = new ApiError(message, 429, data);
        error.retryAfterSeconds = retrySeconds;
        throw error;
      }

      // Parse JSON response
      let data;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // Handle non-2xx responses
      if (!response.ok) {
        const errorMessage =
          data?.message ||
          data?.Message ||
          `Request failed with status ${response.status}`;
        console.log("[API Client] Error response:", errorMessage);
        throw new ApiError(errorMessage, response.status, data);
      }

      console.log("[API Client] Success response for", endpoint);
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      // Network or other errors
      console.error("[API Client] Network or other error:", error);
      throw new ApiError(
        error.message || "Network error. Please check your connection.",
        0,
        null,
      );
    }
  },

  /**
   * GET request
   */
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(
        ([_, v]) => v !== undefined && v !== null && v !== "",
      ),
    ).toString();

    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, { method: "GET" });
  },

  /**
   * POST request
   */
  async post(endpoint, body = {}) {
    return this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /**
   * PUT request
   */
  async put(endpoint, body = {}) {
    return this.request(endpoint, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  /**
   * PATCH request
   */
  async patch(endpoint, body = {}) {
    return this.request(endpoint, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  /**
   * DELETE request
   */
  async delete(endpoint, params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(
        ([_, v]) => v !== undefined && v !== null && v !== "",
      ),
    ).toString();

    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, { method: "DELETE" });
  },
};

// Export as both named and default for compatibility
export default apiClient;
